import { View, ViewHeader } from '@/components/View'
import { type ViewProps } from '@/types/index'
import { BookAIcon, CheckIcon, PencilIcon, PlusIcon, TrashIcon } from '@ttab/elephant-ui/icons'
import { cn } from '@ttab/elephant-ui/utils'
import { useRegistry } from '@/hooks/useRegistry'
import { useSession } from 'next-auth/react'
import { useCallback, useEffect, useState, type JSX } from 'react'
import { toast } from 'sonner'
import { type Hypenation } from '@ttab/elephant-tt-api/baboon'
import { Button, ScrollArea } from '@ttab/elephant-ui'
import { Prompt } from '@/components/Prompt'
import { useQuery } from '@/hooks/useQuery'
import { Pagination } from '@/components/Table/Pagination'
import { useTranslation } from 'react-i18next'

const HypenationItem = ({ isNew, setIsNew, word, hypenated, ignore, handleListHyphenations }: { isNew: boolean, setIsNew: (isNew: boolean) => void, word: string, hypenated: string, ignore: boolean, handleListHyphenations: () => void }) => {
  const { t } = useTranslation('print')
  const [_word, setWord] = useState(word)
  const [_hypenated, setHypenated] = useState(hypenated)
  const [_ignore, setIgnore] = useState(ignore)
  const { baboon } = useRegistry()
  const { data: session } = useSession()
  const [editMode, setEditMode] = useState(isNew ? true : false)
  const [promptIsOpen, setPromptIsOpen] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [defaultWord] = useState(word)

  return (
    <div className='group/edit grid grid-cols-7 gap-x-2 gap-y-0 items-center hover:bg-gray-100 dark:hover:bg-table-focused px-2' onClick={() => setEditMode(true)}>
      {promptIsOpen && (
        <Prompt
          title={t('dictionary.confirmDelete.title')}
          description={t('dictionary.confirmDelete.description')}
          primaryLabel={t('dictionary.confirmDelete.primary')}
          secondaryLabel={t('dictionary.confirmDelete.cancel')}
          disablePrimary={isRemoving}
          onPrimary={() => {
            setIsRemoving(true)
            baboon?.removeHypenation({
              language: 'sv',
              word: _word
            }, session?.accessToken || '')
              .then(() => {
                handleListHyphenations()
              })
              .catch((error) => {
                console.error('Failed to remove hyphenation:', error)
                toast.error(t('dictionary.errors.removeHyphenation'))
              })
              .finally(() => {
                setIsRemoving(false)
                setPromptIsOpen(false)
              })
          }}
          onSecondary={() => {
            setPromptIsOpen(false)
          }}
        />
      )}
      {editMode
        ? (
            <>
              <input
                disabled={!editMode && !isNew}
                type='text'
                value={_word}
                onClick={(e) => {
                  e.stopPropagation()
                  setEditMode(true)
                }}
                onChange={(e) => setWord(e.target.value)}
                className='col-span-2 border border-gray-300 rounded-md px-2 py-1'
              />
              <input
                disabled={!editMode}
                type='text'
                value={_hypenated}
                onChange={(e) => setHypenated(e.target.value)}
                className='col-span-2 border border-gray-300 rounded-md px-2 py-1'
              />
              <label className='col-span-1 flex items-center gap-2'>
                <input type='checkbox' className='w-4 h-4' checked={_ignore} onChange={(e) => setIgnore(e.target.checked)} />
              </label>
              <div className='col-span-2 flex items-end justify-end gap-2'>
                <Button
                  size='sm'
                  onClick={(e) => {
                    e.stopPropagation()
                    const remove = defaultWord.trim() !== ''
                      ? baboon?.removeHypenation({
                        language: 'sv',
                        word: defaultWord
                      }, session?.accessToken || '')
                      : Promise.resolve()

                    remove
                      ?.then(() => baboon?.setHypenation({
                        language: 'sv',
                        word: _word,
                        hypenated: _hypenated,
                        ignore: _ignore
                      }, session?.accessToken || ''))
                      .then(() => {
                        handleListHyphenations()
                        setEditMode(false)
                        setIsNew(false)
                      })
                      .catch((error) => {
                        console.error('Failed to save hyphenation:', error)
                        toast.error(t('dictionary.errors.saveHyphenation'))
                      })
                  }}
                >
                  {t('dictionary.save')}
                </Button>
                {!isNew && (
                  <Button
                    className='bg-red-500 hover:bg-red-400'
                    variant='ghost'
                    size='sm'
                    onClick={(e) => {
                      e.stopPropagation()
                      setPromptIsOpen(true)
                    }}
                  >
                    <TrashIcon strokeWidth={1.75} size={18} className='text-white' />
                  </Button>
                )}
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={(e) => {
                    e.stopPropagation()
                    setEditMode(false)
                    setIsNew(false)
                  }}
                >
                  {t('dictionary.cancel')}
                </Button>
              </div>
            </>
          )
        : (
            <>
              <div className='text-sm w-full col-span-2'>
                {_word}
              </div>
              <div className='text-sm w-full col-span-2'>
                {_hypenated}
              </div>
              <div className='text-sm w-full col-span-1'>
                {_ignore ? <CheckIcon strokeWidth={1.75} size={18} /> : ''}
              </div>
              <div className='invisible group-hover/edit:visible col-span-2 flex items-end justify-end gap-2'>
                <Button variant='ghost' size='sm' onClick={() => setEditMode(true)}>
                  <PencilIcon strokeWidth={1.75} size={18} />
                </Button>
              </div>
            </>
          )}
    </div>
  )
}

const Dictionary = ({ className }: ViewProps): JSX.Element => {
  const { t } = useTranslation('print')
  const { baboon } = useRegistry()
  const { data: session } = useSession()
  const [hyphenations, setHyphenations] = useState<Hypenation[]>([])
  const [isNew, setIsNew] = useState(false)
  const [query] = useQuery(['page'])
  const page = query?.page?.[0]

  const handleListHyphenations = useCallback(async () => {
    if (!session?.accessToken) {
      toast.error(t('dictionary.errors.noToken'))
      return
    }

    if (!baboon) {
      toast.error(t('dictionary.errors.fetchDictionary'))
      return
    }
    try {
      const hyphenations = await baboon.listHypenations({
        language: 'sv',
        page: BigInt(parseInt(page || '1') - 1)
      }, session.accessToken)
      setHyphenations(hyphenations?.response?.items || [])
    } catch (ex) {
      console.error('Error listing hyphenations:', ex)
      toast.error(t('dictionary.errors.listHyphenations'))
    }
  }, [session?.accessToken, baboon, page, t])

  useEffect(() => {
    void handleListHyphenations()
  }, [handleListHyphenations])

  return (
    <View.Root className={cn(className, 'min-h-[900px]')}>
      <ViewHeader.Root>
        <ViewHeader.Content>
          <div className='flex h-full items-center space-x-2 font-bold'>
            <ViewHeader.Title name='dictionary' title={t('dictionary.title')} icon={BookAIcon} iconColor='#006bb3' />
          </div>
          <div className='flex items-center justify-end mt-4'>
            <Button
              onClick={() => setIsNew(!isNew)}
              size='sm'
              className='mb-4 flex items-center gap-2'
            >
              <PlusIcon strokeWidth={1.75} size={18} />
              {t('dictionary.new')}
            </Button>
          </div>
        </ViewHeader.Content>
        <ViewHeader.Action />
      </ViewHeader.Root>
      <View.Content className='p-2 w-full'>
        <div className='flex flex-col gap-2'>
          <div className='grid grid-cols-7 gap-2 mx-2'>
            <h3 className='font-bold text-sm col-span-2'>
              {t('dictionary.columns.word')}
            </h3>
            <h3 className='font-bold text-sm col-span-2'>
              {t('dictionary.columns.hyphenation')}
            </h3>
            <h3 className='font-bold text-sm col-span-1'>
              {t('dictionary.columns.noHyphenate')}
            </h3>
            <h3 className='font-bold text-sm col-span-2'></h3>
          </div>
          <ScrollArea className='h-[calc(100vh-10rem)]'>
            <ul className='flex flex-col gap-2'>
              {isNew && <HypenationItem isNew={isNew} setIsNew={setIsNew} word='' hypenated='' ignore={false} handleListHyphenations={() => { void handleListHyphenations() }} />}
              {hyphenations.map((hyphenation) => (
                <HypenationItem key={hyphenation.word} isNew={false} setIsNew={setIsNew} ignore={hyphenation.ignore} word={hyphenation.word} hypenated={hyphenation.hypenated} handleListHyphenations={() => { void handleListHyphenations() }} />
              ))}
            </ul>
          </ScrollArea>
          <footer className='sticky bottom-0 bg-white'>
            <Pagination />
          </footer>
        </div>
      </View.Content>
    </View.Root>
  )
}

export default Dictionary
